import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Button,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Property } from "../../types/General";
import {
  useAddPropertyMutation,
  useLazyDeletePropertyQuery,
  useLazyGetPropertyQuery,
  useUpdateCustomerMutation,
  useUpdatePropertyMutation,
} from "../../services/customer";
import { showError, showToast, showWarning } from "../../constants/toasts";
import { CUSTOMER_ONBOARDING_STEP, PROPERTY_TYPE } from "../../utils/enums";
import { useNavigate } from "react-router-dom";
import UpdateProperty from "../../Modals/updateProperty";
import Loader from "../../constants/Loader";

type props = {
  userId: string | undefined;
  setValueTabs: Dispatch<SetStateAction<number>>;
  update?: boolean;
};

const CustomerProperty = ({ userId, setValueTabs, update }: props) => {
  const navigate = useNavigate();
  const [addProperty] = useAddPropertyMutation();
  const [updatePropertyMethod] = useUpdatePropertyMutation();
  const [getProperty] = useLazyGetPropertyQuery();
  const [deleteProperty] = useLazyDeletePropertyQuery();
  const [updateCustomer] = useUpdateCustomerMutation();

  const [selectField1, setSelectField1] = useState("default");
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addMoreProperty, setAddMoreProperty] = useState(true);
  const [isAddingMoreProperties, setIsAddingMoreProperties] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null
  );
  const [openUpdate, setOpenUpdate] = useState<boolean>(false);

  const [propertyName, setPropertyName] = useState<string>("");
  const [selectFieldUpdate, setSelectFieldUpdate] = useState("default");
  const [address, setAddress] = useState<string>("");
  const [city, setCity] = useState<string>("");

  const handleChange1 = (event: SelectChangeEvent) => {
    setSelectField1(event.target.value as string);
  };

  const getAllProperty = async () => {
    try {
      const response = await getProperty({
        page: 1,
        limit: 10,
        search: "",
        role: 1,
        userId,
      }).unwrap();
      if (response?.statusCode === 200) {
        // setIsLoading(false);
        setProperties(response?.data?.list || []);
        // setCount(response?.data?.totalCount);
      }
    } catch (error) {
      // setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setIsLoading(true);
    const body = {
      propertyId: id || "",
    };
    try {
      const response = await deleteProperty(body).unwrap();
      setIsLoading(false);

      if (response?.statusCode === 200) {
        getAllProperty();
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setIsLoading(false);
    }
  };

  const editDelete = async (id: string) => {
    setIsLoading(true);
    // const body = {
    //   propertyId: id || "",
    // };
    try {
      // const response = await deleteProperty(body).unwrap();
      setIsLoading(false);

      // if (response?.statusCode === 200) {
      //   getAllProperty();
      // }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setIsLoading(false);
    }
  };

  const handleUpdateProperty = async () => {
    setError(true);
    if (!propertyName || selectFieldUpdate === "default" || !address || !city) {
      return;
    }
    const body = {
      name: propertyName || "",
      type: Number(selectFieldUpdate) || "",
      address: address || "",
      city: city || "",
      userId,
      propertyId: selectedProperty?._id || "",
    };
    try {
      let response;

      response = await updatePropertyMethod(body).unwrap();
      if (response?.statusCode === 200) {
        showToast("Property updated successfully");
        getAllProperty();
        setSelectFieldUpdate("default"); // Reset the select field
        setPropertyName("");
        setAddress("");
        setCity("");
        setError(false); // Clear error
        setOpenUpdate(false);
        setSelectedProperty(null);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const handleNext = async (type: string) => {
    const body = {
      onboardingStep: CUSTOMER_ONBOARDING_STEP.PROPERTY_DETAIL || 2,
      userId,
    };
    try {
      let response;

      response = await updateCustomer(body).unwrap();
      if (response?.statusCode === 200) {
        if (type === "next") {
          // showToast("Property added successfully");
          setValueTabs((prevIndex) => prevIndex + 1);
        } else {
          // showToast("Property updated successfully");
          navigate(-1);
        }
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      propertyName: "",
      address: "",
      city: "",
    },

    validationSchema: Yup.object({
      propertyName: Yup.string()
        .required("This field is required")
        .min(2, "Minimum 2 characters are required"),
      address: Yup.string()
        .required("This field is required")
        .min(3, "Minimum 3 characters are required"),
      city: Yup.string()
        .required("This field is required")
        .min(3, "Minimum 3 characters are required"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      // Create a new property object
      const body = {
        name: values.propertyName,
        type: selectField1,
        address: values.address,
        city: values.city,
        userId,
      };

      try {
        let response;

        response = await addProperty(body).unwrap();

        if (response?.statusCode === 200) {
          // showToast("Property added successfully");
          formik.resetForm();
          getAllProperty();
          setAddMoreProperty(false);
          setSelectField1("default"); // Reset the select field
          setError(false); // Clear error

          if (!isAddingMoreProperties) {
            await handleNext("next");
          }
        }
      } catch (error: any) {
        showError(error?.data?.message || "");
      }
    },
  });

  useEffect(() => {
    if (userId) {
      getAllProperty();
    }
  }, [userId]);

  // Custom function to handle the Add More Properties click

  const handleAddMorePropertiesClick = (
    e: React.MouseEvent<HTMLSpanElement>
  ) => {
    e.preventDefault();
    setIsAddingMoreProperties(true);
    setError(true);
    formik.handleSubmit();
  };

  useEffect(() => {
    if (selectedProperty !== null) {
      setPropertyName(selectedProperty?.name || "");
      setAddress(selectedProperty?.address || "");
      setCity(selectedProperty?.city || "");
      setSelectFieldUpdate(String(selectedProperty?.type) || "");
    }
  }, [selectedProperty]);

  return (
    <div className="form">
      {/* <Loader load/> */}
      {properties?.length ? (
        <div style={{ marginBottom: "20px" }}>
          <Typography variant="h6">Added Properties:</Typography>
          <div>
            {properties?.map((item, index) => (
              <div className="borderBox">
                <IconButton
                  className="edit_icon"
                  onClick={() => {
                    setSelectedProperty(item);
                    setOpenUpdate(true);
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  className="remove_icon"
                  onClick={() => handleDelete(item?._id || "")}
                >
                  <CloseIcon />
                </IconButton>
                <div className="gap_p">
                  <div className="control_group w_50">
                    <label>Property Name</label>
                    <TextField
                      placeholder="Enter Property Name"
                      hiddenLabel
                      fullWidth
                      className="text_field"
                      name="propertyName"
                      id="propertyName"
                      value={item?.name}
                      disabled
                      inputProps={{ maxLength: 80 }}
                    />
                  </div>
                  <div className="control_group w_50">
                    <label>Property Type</label>
                    <Select
                      fullWidth
                      labelId="country_to"
                      id="country_to"
                      value={String(item?.type)}
                      onChange={handleChange1}
                      disabled
                    >
                      <MenuItem disabled value="default">
                        Select Property
                      </MenuItem>
                      <MenuItem value={PROPERTY_TYPE.HOME}>Home</MenuItem>
                      <MenuItem value={PROPERTY_TYPE.VILLA}>Villa</MenuItem>
                      <MenuItem value={PROPERTY_TYPE.APARTMENT}>
                        Apartment
                      </MenuItem>
                      <MenuItem value={PROPERTY_TYPE.BUILDING}>
                        Building
                      </MenuItem>
                    </Select>
                  </div>
                  <div className="control_group w_50">
                    <label>Address</label>
                    <TextField
                      placeholder="Enter Address"
                      hiddenLabel
                      fullWidth
                      className="text_field"
                      name="address"
                      id="address"
                      inputProps={{ maxLength: 80 }}
                      value={item?.address}
                      disabled
                    />
                  </div>
                  <div className="control_group w_50">
                    <label>City</label>
                    <TextField
                      placeholder="Enter City"
                      hiddenLabel
                      fullWidth
                      className="text_field"
                      name="city"
                      id="city"
                      inputProps={{ maxLength: 80 }}
                      value={item?.city}
                      disabled
                    />
                  </div>
                </div>
                {/* <p>Update</p> */}
                {/* <IconButton
                  className="edit_icon"
                  onClick={() => editDelete(item?._id || "")}
                >
                  <EditIcon />
                </IconButton> */}
              </div>
            ))}
          </div>
          {!addMoreProperty ? (
            <p className="link_text end" style={{ paddingTop: "20px" }}>
              <span
                className="c_primary"
                onClick={() => {
                  setAddMoreProperty(true);
                  setIsAddingMoreProperties(false);
                }}
              >
                <AddIcon /> Add Property
              </span>
            </p>
          ) : null}
        </div>
      ) : null}

      {/* {addMoreProperty ? ( */}
      <form onSubmit={formik.handleSubmit}>
        {addMoreProperty ? (
          <>
            <div className="borderBox">
              {properties?.length > 0 ? (
                <IconButton
                  className="remove_icon"
                  onClick={() => {
                    setError(false);
                    setAddMoreProperty(false);
                  }}
                >
                  <RemoveIcon />
                </IconButton>
              ) : null}

              <div className="gap_p">
                <div className="control_group w_50">
                  <label>Property Name</label>
                  <TextField
                    placeholder="Enter Property Name"
                    hiddenLabel
                    fullWidth
                    className="text_field"
                    name="propertyName"
                    id="propertyName"
                    inputProps={{ maxLength: 80 }}
                    value={formik.values.propertyName}
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else {
                        formik.handleChange(val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    helperText={
                      formik.touched.propertyName && formik.errors.propertyName
                    }
                  />
                </div>
                <div className="control_group w_50">
                  <label>Property Type</label>
                  <Select
                    fullWidth
                    labelId="country_to"
                    id="country_to"
                    value={selectField1}
                    onChange={handleChange1}
                  >
                    <MenuItem disabled value="default">
                      Select Property
                    </MenuItem>
                    <MenuItem value={PROPERTY_TYPE.HOME}>Home</MenuItem>
                    <MenuItem value={PROPERTY_TYPE.VILLA}>Villa</MenuItem>
                    <MenuItem value={PROPERTY_TYPE.APARTMENT}>
                      Apartment
                    </MenuItem>
                    <MenuItem value={PROPERTY_TYPE.BUILDING}>Building</MenuItem>
                  </Select>
                  {selectField1 === "default" && error ? (
                    <Typography
                      className="text_field"
                      style={{ color: "red", fontSize: "12px" }}
                    >
                      This field is required
                    </Typography>
                  ) : null}
                </div>
                <div className="control_group w_50">
                  <label>Address</label>
                  <TextField
                    placeholder="Enter Address"
                    hiddenLabel
                    fullWidth
                    className="text_field"
                    name="address"
                    id="address"
                    inputProps={{ maxLength: 80 }}
                    value={formik.values.address}
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else {
                        formik.handleChange(val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    helperText={formik.touched.address && formik.errors.address}
                  />
                </div>
                <div className="control_group w_50">
                  <label>City</label>
                  <TextField
                    placeholder="Enter City"
                    hiddenLabel
                    fullWidth
                    className="text_field"
                    name="city"
                    id="city"
                    inputProps={{ maxLength: 80 }}
                    value={formik.values.city}
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else {
                        formik.handleChange(val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    helperText={formik.touched.city && formik.errors.city}
                  />
                </div>
              </div>
            </div>
            <p className="link_text end">
              <span
                className="c_primary"
                onClick={handleAddMorePropertiesClick}
              >
                <AddIcon /> Add Property
              </span>
            </p>
          </>
        ) : null}

        <div className="form_btn">
          <Button
            className="btn btn_primary"
            type={!properties?.length ? "submit" : "button"}
            onClick={() => {
              if (!properties?.length) {
                setError(true);
              } else {
                handleNext("next");
              }
            }}
          >
            Next
          </Button>
          {update ? (
            <Button
              className="btn btn_primary"
              onClick={() => handleNext("submit")}
              type="button"
            >
              Submit
            </Button>
          ) : null}
        </div>
      </form>
      <UpdateProperty
        open={openUpdate}
        onClose={() => setOpenUpdate(!openUpdate)}
        setOpen={setOpenUpdate}
        error={error}
        setPropertyName={setPropertyName}
        propertyName={propertyName}
        selectFieldUpdate={selectFieldUpdate}
        setSelectFieldUpdate={setSelectFieldUpdate}
        setAddress={setAddress}
        address={address}
        setCity={setCity}
        city={city}
        handleUpdateProperty={handleUpdateProperty}
      />
    </div>
  );
};

export default CustomerProperty;
